import {
  COUNCIL_DATASET_CSV_PATH,
  COUNCIL_REPORT_MANIFEST,
  COUNCIL_REPORT_MANIFEST_BY_SLUG,
  DFT_RATINGS_SOURCE,
} from '@fixtyer/content';
import { Link, Outlet, useLocation } from 'react-router';

function filterLink(key: 'region' | 'rating', value: string): string {
  const query = new URLSearchParams({ [key]: value });
  return `/authority-ratings?${query.toString()}`;
}

function reviewedDate(value: string): string {
  const [year, month, day] = value.split('-');
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${Number(day)} ${months[Number(month) - 1]} ${year}`;
}

function correctionLabel(status: 'no-correction-recorded' | 'corrected'): string {
  return status === 'corrected' ? 'Correction recorded' : 'No correction recorded';
}

function comparableReports(slug: string): typeof COUNCIL_REPORT_MANIFEST {
  const current = COUNCIL_REPORT_MANIFEST_BY_SLUG.get(slug);
  if (!current) return [];
  const candidates = COUNCIL_REPORT_MANIFEST.filter(
    (entry) => entry.slug !== slug && entry.reportUrl
  );
  const preferred = candidates.filter(
    (entry) =>
      entry.region === current.region &&
      entry.ratings.overall === current.ratings.overall
  );
  const sameRating = candidates.filter(
    (entry) =>
      entry.ratings.overall === current.ratings.overall &&
      !preferred.includes(entry)
  );
  const sameRegion = candidates.filter(
    (entry) => entry.region === current.region && !preferred.includes(entry)
  );
  return [...preferred, ...sameRating, ...sameRegion, ...candidates]
    .filter((entry, index, entries) => entries.indexOf(entry) === index)
    .slice(0, 2);
}

function CouncilEvidenceSection({ slug }: { slug: string }) {
  const report = COUNCIL_REPORT_MANIFEST_BY_SLUG.get(slug);
  if (!report?.reportUrl) return null;
  const comparable = comparableReports(slug);

  return (
    <section className="bg-surface-50 border-y border-surface-200" aria-labelledby="council-evidence-heading">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-sm font-semibold text-brand-700">Reusable evidence record</p>
        <h2 id="council-evidence-heading" className="font-display text-3xl font-bold text-brand-950 mt-2">
          Council rating evidence and provenance
        </h2>
        <p className="mt-3 text-surface-700">
          Rating period: <strong>{report.ratingPeriod}</strong> · Reviewed{' '}
          <strong>{reviewedDate(report.reviewedDate)}</strong>
        </p>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <article className="rounded-xl border border-surface-200 bg-white p-5">
            <h3 className="font-semibold text-brand-950">Source</h3>
            <p className="mt-2 text-sm text-surface-700">
              <a className="text-brand-700 underline" href={DFT_RATINGS_SOURCE.url}>
                Department for Transport — {DFT_RATINGS_SOURCE.title}
              </a>
            </p>
          </article>
          <article className="rounded-xl border border-surface-200 bg-white p-5">
            <h3 className="font-semibold text-brand-950">Observed DfT fact</h3>
            <p className="mt-2 text-sm text-surface-700">
              Overall {report.ratings.overall.toUpperCase()}; condition{' '}
              {report.ratings.condition?.toUpperCase() ?? 'not scored'}; spend{' '}
              {report.ratings.spend?.toUpperCase() ?? 'not scored'}; best practice{' '}
              {report.ratings.bestPractice?.toUpperCase() ?? 'not scored'}.
            </p>
          </article>
          <article className="rounded-xl border border-surface-200 bg-white p-5">
            <h3 className="font-semibold text-brand-950">Cautious interpretation</h3>
            <p className="mt-2 text-sm text-surface-700">
              This is network-level context. The rating does not determine liability for a
              particular incident or establish what was known about a specific defect.
            </p>
          </article>
        </div>

        <p className="mt-5 text-sm text-surface-600">
          Correction status:{' '}
          <a className="text-brand-700 underline" href={report.correction.url}>
            {correctionLabel(report.correction.status)}
          </a>.
        </p>

        <nav className="mt-8" aria-label={`Related evidence for ${report.displayName}`}>
          <h3 className="font-semibold text-brand-950 mb-3">Compare and continue</h3>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <li><Link className="text-brand-700 underline" to={filterLink('region', report.region)}>More in {report.region}</Link></li>
            <li><Link className="text-brand-700 underline" to={filterLink('rating', report.ratings.overall)}>All {report.ratings.overall.toUpperCase()} ratings</Link></li>
            {comparable.map((entry) => (
              <li key={entry.slug}>
                <Link className="text-brand-700 underline" to={entry.reportUrl!}>
                  Compare {entry.displayName}
                </Link>
              </li>
            ))}
            <li><Link className="text-brand-700 underline" to="/road-maintenance-report">National table and report</Link></li>
            <li><Link className="text-brand-700 underline" to="/guides/section-58-defence">Section 58 guide</Link></li>
            <li><Link className="text-brand-700 underline" to="/guides/pothole-claim-letter-template">Free pothole claim letter template</Link></li>
            <li><Link className="text-brand-700 underline" to="/guides/request-road-inspection-records">Request this road&apos;s inspection records</Link></li>
            <li><a className="text-brand-700 underline" href={COUNCIL_DATASET_CSV_PATH}>Download the dataset</a></li>
            <li><Link className="text-brand-700 underline" to="/check">Free eligibility check</Link></li>
          </ul>
        </nav>
      </div>
    </section>
  );
}

export function CouncilReportsLayout() {
  const { pathname } = useLocation();
  const slug = pathname.match(/^\/councils\/([^/]+)$/)?.[1];

  return (
    <>
      <Outlet />
      {slug && <CouncilEvidenceSection slug={slug} />}
    </>
  );
}
